/**
 * User Service
 * Kullanıcı yönetimi işlemleri için service
 */

import { apiClient } from '../client/apiClient';
import { ENDPOINTS, createEndpoint } from '../config/endpoints';
import {
  UserProfile,
  UserRole,
  PaginatedResponse,
  BaseQueryParams,
  ID,
  ExportFormat
} from '../types/api.types';

/**
 * User query parameters
 */
export interface UserQueryParams extends BaseQueryParams {
  role?: UserRole | UserRole[];
  tenantId?: ID | ID[];
  isActive?: boolean;
  isVerified?: boolean;
  lastLoginFrom?: Date;
  lastLoginTo?: Date;
  includePermissions?: boolean;
  includeSessions?: boolean;
}

/**
 * User create/update request
 */
export interface UserRequest {
  email: string;
  password?: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: UserRole;
  tenantId?: ID;
  isActive?: boolean;
  permissions?: string[];
  sendInvite?: boolean;
}

/**
 * User password change request
 */
export interface UserPasswordChangeRequest {
  userId: ID;
  newPassword: string;
  requirePasswordChange?: boolean;
}

/**
 * User permission update request
 */
export interface UserPermissionRequest {
  userId: ID;
  permissions: string[];
}

/**
 * User session
 */
export interface UserSession {
  id: string;
  deviceId: string;
  deviceType: string;
  platform: string;
  ipAddress: string;
  lastActivity: string;
  createdAt: string;
  isCurrent: boolean;
}

/**
 * User statistics
 */
export interface UserStatistics {
  totalUsers: number;
  activeUsers: number;
  usersByRole: Record<UserRole, number>;
  newUsersToday: number;
  newUsersThisWeek: number;
  usersByTenant: Array<{
    tenantId: ID;
    tenantName: string;
    count: number;
  }>;
}

/**
 * User Service Interface
 */
export interface IUserService {
  // CRUD Operations
  getUsers(params?: UserQueryParams): Promise<PaginatedResponse<UserProfile>>;
  getUser(id: ID): Promise<UserProfile>;
  createUser(user: UserRequest): Promise<UserProfile>;
  updateUser(id: ID, user: Partial<UserRequest>): Promise<UserProfile>;
  deleteUser(id: ID): Promise<void>;
  
  // Special Queries
  getUserByEmail(email: string): Promise<UserProfile>;
  searchUsers(query: string, params?: BaseQueryParams): Promise<PaginatedResponse<UserProfile>>;
  getUsersByRole(role: UserRole, params?: BaseQueryParams): Promise<PaginatedResponse<UserProfile>>;
  getUsersByTenant(tenantId: ID, params?: BaseQueryParams): Promise<PaginatedResponse<UserProfile>>;
  
  // User Management
  changeUserPassword(request: UserPasswordChangeRequest): Promise<void>;
  updateUserPermissions(request: UserPermissionRequest): Promise<void>;
  activateUser(userId: ID): Promise<void>;
  deactivateUser(userId: ID): Promise<void>;
  resendInvite(email: string): Promise<void>;
  
  // Session Management
  getUserSessions(userId: ID): Promise<UserSession[]>;
  revokeUserSession(userId: ID, sessionId: ID): Promise<void>;
  revokeAllUserSessions(userId: ID): Promise<void>;
  
  // Statistics & Reports
  getUserStatistics(): Promise<UserStatistics>;
  getUserActivity(userId: ID, dateRange?: { startDate: Date; endDate: Date }): Promise<any>;
  exportUsers(format: ExportFormat, params?: UserQueryParams): Promise<Blob>;
  
  // Profile Management
  updateUserProfile(userId: ID, profile: Partial<UserProfile>): Promise<UserProfile>;
  uploadUserAvatar(userId: ID, file: File): Promise<string>;
}

/**
 * User Service Implementation
 */
export class UserService implements IUserService {
  async getUsers(params?: UserQueryParams): Promise<PaginatedResponse<UserProfile>> {
    try {
      const endpoint = ENDPOINTS.USERS.LIST;
      const cacheKey = this.generateCacheKey('users', params);
      
      return await apiClient.getList<UserProfile>(endpoint, params, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000 // 5 dakika
      });
    } catch (error) {
      console.error('Get users error:', error);
      throw error;
    }
  }

  async getUser(id: ID): Promise<UserProfile> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.USERS.DETAIL, id);
      const cacheKey = `user_${id}`;
      
      const response = await apiClient.get<UserProfile>(endpoint, id, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get user ${id} error:`, error);
      throw error;
    }
  }

  async createUser(user: UserRequest): Promise<UserProfile> {
    try {
      const endpoint = ENDPOINTS.USERS.CREATE;
      const response = await apiClient.customPost<UserProfile>(endpoint, user);
      
      this.clearUsersCache();
      
      return response.data;
    } catch (error) {
      console.error('Create user error:', error);
      throw error;
    }
  }

  async updateUser(id: ID, user: Partial<UserRequest>): Promise<UserProfile> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.USERS.UPDATE, id);
      const response = await apiClient.customPut<UserProfile>(endpoint, user);
      
      this.clearUserCache(id);
      this.clearUsersCache();
      
      return response.data;
    } catch (error) {
      console.error(`Update user ${id} error:`, error);
      throw error;
    }
  }

  async deleteUser(id: ID): Promise<void> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.USERS.DELETE, id);
      await apiClient.delete(endpoint, id);
      
      this.clearUserCache(id);
      this.clearUsersCache();
    } catch (error) {
      console.error(`Delete user ${id} error:`, error);
      throw error;
    }
  }

  async getUserByEmail(email: string): Promise<UserProfile> {
    try {
      const endpoint = `${ENDPOINTS.USERS.BASE}/by-email/${encodeURIComponent(email)}`;
      const cacheKey = `user_by_email_${email}`;
      
      const response = await apiClient.customGet<UserProfile>(endpoint, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get user by email ${email} error:`, error);
      throw error;
    }
  }

  async searchUsers(query: string, params?: BaseQueryParams): Promise<PaginatedResponse<UserProfile>> {
    try {
      const endpoint = `${ENDPOINTS.USERS.BASE}/search`;
      const searchParams = {
        ...params,
        q: query
      };
      
      const cacheKey = this.generateCacheKey('users_search', { query, ...params });
      
      return await apiClient.getList<UserProfile>(endpoint, searchParams, {
        cacheKey,
        cacheTTL: 60 * 1000
      });
    } catch (error) {
      console.error('Search users error:', error);
      throw error;
    }
  }

  async getUsersByRole(role: UserRole, params?: BaseQueryParams): Promise<PaginatedResponse<UserProfile>> {
    const roleParams: UserQueryParams = {
      ...params,
      role
    };
    
    return this.getUsers(roleParams);
  }

  async getUsersByTenant(tenantId: ID, params?: BaseQueryParams): Promise<PaginatedResponse<UserProfile>> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.USERS.TENANT_USERS, tenantId);
      const cacheKey = this.generateCacheKey(`users_tenant_${tenantId}`, params);
      
      return await apiClient.getList<UserProfile>(endpoint, params, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000
      });
    } catch (error) {
      console.error(`Get users by tenant ${tenantId} error:`, error);
      throw error;
    }
  }

  async changeUserPassword(request: UserPasswordChangeRequest): Promise<void> {
    try {
      const endpoint = `${ENDPOINTS.USERS.BASE}/${request.userId}/change-password`;
      
      await apiClient.customPost<void>(endpoint, {
        newPassword: request.newPassword,
        requirePasswordChange: request.requirePasswordChange
      });
    } catch (error) {
      console.error(`Change user password ${request.userId} error:`, error);
      throw error;
    }
  }

  async updateUserPermissions(request: UserPermissionRequest): Promise<void> {
    try {
      const endpoint = `${ENDPOINTS.USERS.BASE}/${request.userId}/permissions`;
      
      await apiClient.customPut<void>(endpoint, {
        permissions: request.permissions
      });
      
      this.clearUserCache(request.userId);
    } catch (error) {
      console.error(`Update user permissions ${request.userId} error:`, error);
      throw error;
    }
  }

  async activateUser(userId: ID): Promise<void> {
    try {
      const endpoint = `${ENDPOINTS.USERS.BASE}/${userId}/activate`;
      
      await apiClient.customPost<void>(endpoint, {});
      
      this.clearUserCache(userId);
      this.clearUsersCache();
    } catch (error) {
      console.error(`Activate user ${userId} error:`, error);
      throw error;
    }
  }

  async deactivateUser(userId: ID): Promise<void> {
    try {
      const endpoint = `${ENDPOINTS.USERS.BASE}/${userId}/deactivate`;
      
      await apiClient.customPost<void>(endpoint, {});
      
      this.clearUserCache(userId);
      this.clearUsersCache();
    } catch (error) {
      console.error(`Deactivate user ${userId} error:`, error);
      throw error;
    }
  }

  async resendInvite(email: string): Promise<void> {
    try {
      const endpoint = `${ENDPOINTS.USERS.BASE}/resend-invite`;
      
      await apiClient.customPost<void>(endpoint, { email });
    } catch (error) {
      console.error(`Resend invite ${email} error:`, error);
      throw error;
    }
  }

  async getUserSessions(userId: ID): Promise<UserSession[]> {
    try {
      const endpoint = `${ENDPOINTS.USERS.BASE}/${userId}/sessions`;
      const cacheKey = `user_sessions_${userId}`;
      
      const response = await apiClient.customGet<UserSession[]>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 1000
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get user sessions ${userId} error:`, error);
      throw error;
    }
  }

  async revokeUserSession(userId: ID, sessionId: ID): Promise<void> {
    try {
      const endpoint = `${ENDPOINTS.USERS.BASE}/${userId}/sessions/${sessionId}/revoke`;
      
      await apiClient.customPost<void>(endpoint, {});
      
      this.clearUserCache(userId);
    } catch (error) {
      console.error(`Revoke user session ${userId}/${sessionId} error:`, error);
      throw error;
    }
  }

  async revokeAllUserSessions(userId: ID): Promise<void> {
    try {
      const endpoint = `${ENDPOINTS.USERS.BASE}/${userId}/sessions/revoke-all`;
      
      await apiClient.customPost<void>(endpoint, {});
      
      this.clearUserCache(userId);
    } catch (error) {
      console.error(`Revoke all user sessions ${userId} error:`, error);
      throw error;
    }
  }

  async getUserStatistics(): Promise<UserStatistics> {
    try {
      const endpoint = `${ENDPOINTS.USERS.BASE}/statistics`;
      const cacheKey = 'user_statistics';
      
      const response = await apiClient.customGet<UserStatistics>(endpoint, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000
      });
      
      return response.data;
    } catch (error) {
      console.error('Get user statistics error:', error);
      throw error;
    }
  }

  async getUserActivity(userId: ID, dateRange?: { startDate: Date; endDate: Date }): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.USERS.BASE}/${userId}/activity`;
      
      const response = await apiClient.customGet<any>(endpoint, {
        cacheTTL: 60 * 1000,
        params: dateRange
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get user activity ${userId} error:`, error);
      throw error;
    }
  }

  async exportUsers(format: ExportFormat, params?: UserQueryParams): Promise<Blob> {
    try {
      const endpoint = `${ENDPOINTS.USERS.BASE}/export`;
      return await apiClient.exportData(endpoint, format, params);
    } catch (error) {
      console.error('Export users error:', error);
      throw error;
    }
  }

  async updateUserProfile(userId: ID, profile: Partial<UserProfile>): Promise<UserProfile> {
    try {
      const endpoint = `${ENDPOINTS.USERS.BASE}/${userId}/profile`;
      
      const response = await apiClient.customPut<UserProfile>(endpoint, profile);
      
      this.clearUserCache(userId);
      
      return response.data;
    } catch (error) {
      console.error(`Update user profile ${userId} error:`, error);
      throw error;
    }
  }

  async uploadUserAvatar(userId: ID, file: File): Promise<string> {
    try {
      const endpoint = `${ENDPOINTS.USERS.BASE}/${userId}/avatar`;
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await apiClient.customPost<{ url: string }>(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      this.clearUserCache(userId);
      
      return response.data.url;
    } catch (error) {
      console.error(`Upload user avatar ${userId} error:`, error);
      throw error;
    }
  }

  // ==================== PRIVATE METHODS ====================

  private generateCacheKey(prefix: string, params?: any): string {
    if (!params) return prefix;
    
    const paramString = JSON.stringify(params);
    const hash = this.hashString(paramString);
    return `${prefix}_${hash}`;
  }

  private hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.codePointAt(i);
      if (char === undefined) continue;
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }

  private clearUserCache(userId: ID): void {
    apiClient.cancelRequest(`user_${userId}`);
  }

  private clearUsersCache(): void {
    apiClient.cancelRequest('users');
  }
}

// Singleton instance
export const userService = new UserService();

// Default export
export default userService;